import React, { useEffect, useState, useMemo } from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  Card,
  CardContent,
  Columns,
  Column,
  Tabs,
  TabList,
  Tab,
  TabLink,
  Notification,
} from 'src/ui/mui-ui-kit';
import dayjs from 'dayjs-ext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Field } from 'react-final-form';
import { toast } from 'react-toastify';
import _ from 'lodash';
import {
  getAccount,
  fetchAccount,
  fetchFormData,
  getFormData,
  signSubmission,
  updateAccountStatus,
  ACCOUNT_STATUS,
} from 'src/account';
import { getUser } from 'src/user';
import {
  setBreadcrumbs,
  reduceServerErrors,
  InnerLoader,
  stripHtml,
  UiToggleAdapter,
  UiInputAdapter,
  UiToastAdapter,
  getLicenseTypes,
  maskPhone,
  maskSsn,
  UiSpacer,
  UiQuillAdapter,
  submitAndReset,
  getFileNameFromUrl,
} from 'src/ui';
import { DATE_MONTH_DAY_YEAR, DATE_DAY_SMONTH_YEAR } from 'src/ui/const';
import { usStates, militaryOptions } from 'src/core';
import { required } from 'src/ui/validations';
import { getConsentTypesForAccount } from 'src/consent/services';
import { getDocumentTypesForAccount } from 'src/document/services';
import { PERSON_TYPE_OWNER } from 'src/user/const';
import { isDataComplete, getSubmissions } from '../services';
import { DataIterator, SubmissionModal, RespondModal } from '../components';
import { findValue } from '../filters';

const REVIEW_TAB_INDEXES = {
  Business: 'Business',
  Locations: 'Locations',
  People: 'People',
  Vendors: 'Vendors',
  Products: 'Products',
  Documents: 'Documents',
  Consents: 'Consents',
  Payment: 'Payment',
  Loans: 'Loans',
  Obligations: 'Obligations',
};

function ReviewAndSubmitListPage({
  user,
  account,
  fetchAccount,
  fetchFormData,
  formData,
  setBreadcrumbs,
  signSubmission,
  updateAccountStatus,
}) {
  const [activeTab, setActiveTab] = useState(REVIEW_TAB_INDEXES.Business);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [consentTypes, setConsentTypes] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [updateAction, setUpdateAction] = useState('');
  const [submissionModal, setSubmissionModal] = useState(false);

  const documentTypes = getDocumentTypesForAccount(account, formData);
  const hasDocuments = documentTypes.length > 0;
  const hasConsents = getConsentTypesForAccount(account, formData).length > 0;
  const hasPayment = account.hasPaymentsPage;

  const hasLoans = account.loans.length > 0;
  const hasObligations = account.loans.length > 0;

  async function onSubmit(values) {
    const response = await signSubmission({
      financialInstitutionId:
        user.financialInstitutionId || user.fiSelectedAccountId,
      accountId: account.id,
      ...values,
    });

    // TODO: remove second condition
    if (!response.ok && response.type !== 'account/sign_submission_success') {
      toast(
        UiToastAdapter(
          'Submission was unsuccessfull. Check your initials and try again.',
          'danger'
        )
      );
      return { initials: 'Incorrect initials' };
    }

    toast(
      UiToastAdapter('Data reviewed and submitted successfully!', 'success')
    );
    setSubmissionModal(true);
    setSubmitted(true);
    return undefined;
  }

  async function onUpdateAccountStatus(values) {
    setUpdateAction('');
    const response = await updateAccountStatus({
      financialInstitutionId: user.financialInstitutionId,
      accountId: user.accountId,
      status: updateAction,
      message: values.explanation,
    });

    // TODO: remove second condition
    if (
      !response.ok &&
      response.type !== 'account/update_account_status_success'
    ) {
      toast(
        UiToastAdapter('Update was unsuccessfull. Try again later.', 'danger')
      );
      return undefined;
    }

    toast(UiToastAdapter('Account updated successfully!', 'success'));
    setUpdateAction('');
    return undefined;
  }

  const licenseTypes = useMemo(() => getLicenseTypes(account, formData), [
    account,
    formData,
  ]);

  const people = useMemo(
    () =>
      (account.people || []).map(person => ({
        ...person,
        fullName: `${person.firstName} ${person.lastName}`,
      })),
    [account]
  );

  const businessDataMap = [
    {
      label: 'Business Name',
      value: 'businessName',
      required: true,
    },
    {
      label: 'Business Since',
      value: 'businessSince',
      required: true,
      filter: value => value && dayjs(value).format(DATE_MONTH_DAY_YEAR),
    },
    {
      label: 'Industry',
      value: 'industries',
      required: true,
      filter: values =>
        values &&
        values
          .map(value => findValue(formData.industries, { id: value }, 'name'))
          .filter(Boolean)
          .join(', '),
    },
    {
      label: 'Tax ID',
      value: 'taxId',
      required: true,
      filter: value =>
        value &&
        `${value.toString().substring(0, 2)}-${value.toString().substring(2)}`,
    },
    {
      label: 'NAICS',
      value: 'naics',
    },
    {
      label: 'Email Address',
      value: 'businessEmail',
      required: true,
    },
    {
      label: 'Customer Service Email Address',
      value: 'customerServiceEmail',
      required: true,
    },
    {
      label: 'Websites',
      value: 'websites',
      required: true,
      filter: value =>
        (value || []).map(v => (
          <>
            <a
              href={v.url.indexOf('http') > -1 ? v.url : `http://${v.url}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {v.url}
            </a>
            &nbsp;&nbsp;
          </>
        )),
    },
    {
      label: 'Primary Phone',
      value: 'primaryPhone',
      required: true,
      filter: value => value && maskPhone(value),
    },
    {
      label: 'Other Phone',
      value: 'otherPhone',
      filter: value => value && maskPhone(value),
    },
    {
      label: 'Customer Service Phone',
      value: 'customerServicePhone',
      required: true,
      filter: value => value && maskPhone(value),
    },
    {
      label: 'Years of Ownership',
      value: 'currentOwnership',
    },
    {
      label: 'Facebook',
      value: 'facebook',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ),
    },
    {
      label: 'Linkedin',
      value: 'linkedin',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ),
    },
    {
      label: 'Twitter',
      value: 'twitter',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ),
    },
    {
      label: 'Instagram',
      value: 'instagram',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ),
    },
    {
      label: 'Brief Business Description',
      value: 'briefDescription',
      required: true,
      filter: value => value && stripHtml(value),
    },
  ];

  const locationsDataMap = [
    {
      label: 'Location Address',
      value: 'address',
      required: true,
    },
    {
      label: 'Location Address 2',
      value: 'address2',
    },
    {
      label: 'City',
      value: 'city',
      required: true,
    },
    {
      label: 'State',
      value: 'state',
      required: true,
      filter: value => value && findValue(usStates, { value }, 'label'),
    },
    {
      label: 'Zip Code',
      value: 'zipCode',
      required: true,
    },
    {
      label: 'Phone',
      value: 'phoneNumber',
      filter: value => value && maskPhone(value),
    },
    {
      label: 'DBA',
      value: 'dba',
    },
    {
      label: 'E-mail',
      value: 'email',
    },
    {
      label: 'Facebook',
      value: 'facebook',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ),
    },
    {
      label: 'Linkedin',
      value: 'linkedin',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ),
    },
    {
      label: 'Twitter',
      value: 'twitter',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ),
    },
    {
      label: 'Instagram',
      value: 'instagram',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ),
    },
    {
      label: 'License Type',
      value: 'licenseType',
      filter: id => id && findValue(licenseTypes, { id }, 'name'),
    },
    {
      label: 'License Number',
      value: 'licenseNumber',
    },
    {
      label: 'Date Issued',
      value: 'licenseIssuedDate',
      filter: value => value && dayjs(value).format(DATE_MONTH_DAY_YEAR),
    },
    {
      label: 'Expiration Date',
      value: 'licenseExpiredDate',
      filter: value => value && dayjs(value).format(DATE_MONTH_DAY_YEAR),
    },
    {
      label: 'License Image - Front',
      value: 'licenseImageFront',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            View <FontAwesomeIcon icon={['fal', 'external-link']} />
          </a>
        ),
    },
    {
      label: 'License Image - Back',
      value: 'licenseImageBack',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            View <FontAwesomeIcon icon={['fal', 'external-link']} />
          </a>
        ),
    },
  ];

  const peopleDataMap = [
    {
      label: 'Type',
      value: 'type',
      required: true,
      filter: _.capitalize,
    },
    {
      label: 'Title',
      value: 'title',
    },
    {
      label: 'Phone',
      value: 'primaryNumber',
      required: true,
      filter: value => value && maskPhone(value),
    },
    {
      label: 'Phone 2',
      value: 'otherNumber',
      filter: value => value && maskPhone(value),
    },
    {
      label: 'Email',
      value: 'emailAddress',
      required: true,
    },
    {
      label: 'Ownership',
      value: 'percentageOwnership',
      required: person => person.type === PERSON_TYPE_OWNER,
      filter: value => value !== null && value !== '' && `${value}%`,
    },
    {
      label: 'SSN',
      value: 'socialSecurity',
      required: true,
      filter: value => value && maskSsn(value),
    },
    {
      label: 'Birthdate',
      value: 'dob',
      required: true,
      filter: value => value && dayjs(value).format(DATE_MONTH_DAY_YEAR),
    },
    {
      label: 'Occupation',
      value: 'occupation',
      required: true,
    },
    {
      label: 'Military',
      value: 'military',
      filter: value => value && findValue(militaryOptions, { value }, 'label'),
    },
    {
      label: 'Citizenship',
      value: 'citizenship',
      required: true,
      // TODO: get list of countries from server
      filter: () => 'United States',
    },

    // Primary address
    {
      label: 'Primary Addr.',
      value: 'primaryAddress',
      required: true,
    },
    {
      label: 'Primary Addr. 2',
      value: 'primaryAddress2',
    },
    {
      label: 'Primary City',
      value: 'primaryCity',
      required: true,
    },
    {
      label: 'Primary State',
      value: 'primaryState',
      required: true,
      filter: value => value && findValue(usStates, { value }, 'label'),
    },
    {
      label: 'Primary Zip',
      value: 'primaryZipcode',
      required: true,
    },

    // Mailing address
    {
      label: 'Mailing Addr.',
      value: 'mailingAddress',
      required: true,
    },
    {
      label: 'Mailing Addr. 2',
      value: 'mailingAddress2',
    },
    {
      label: 'Mailing City',
      value: 'mailingCity',
      required: true,
    },
    {
      label: 'Mailing State',
      value: 'mailingState',
      required: true,
      filter: value => value && findValue(usStates, { value }, 'label'),
    },
    {
      label: 'Mailing Zip',
      value: 'mailingZipcode',
      required: true,
    },

    // Identification
    {
      label: 'License #',
      value: 'licenseNumber',
    },
    {
      label: 'Issuing State',
      value: 'licenseIssuingState',
      filter: value => value && findValue(usStates, { value }, 'label'),
    },
    {
      label: 'Issued Date',
      value: 'licenseIssuedDate',
      filter: value => value && dayjs(value).format(DATE_MONTH_DAY_YEAR),
    },
    {
      label: 'Expiration Date',
      value: 'licenseExpirationDate',
      filter: value => value && dayjs(value).format(DATE_MONTH_DAY_YEAR),
    },
    {
      label: 'License - Front',
      value: 'licenseImageFront',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            View <FontAwesomeIcon icon={['fal', 'external-link']} />
          </a>
        ),
    },
    {
      label: 'License - Back',
      value: 'licenseImageBack',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            View <FontAwesomeIcon icon={['fal', 'external-link']} />
          </a>
        ),
    },
    {
      label: 'Background Check Status',
      value: 'bgStatus',
      required: false,
    },
    // {
    //   label: 'Background Check Verification',
    //   value: 'verification',
    //   required: false,
    // },
    {
      label: 'Background Check Report',
      value: 'bgReport',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            View <FontAwesomeIcon icon={['fal', 'external-link']} />
          </a>
        ),
    },
  ];

  const vendorsDataMap = [
    {
      label: 'Type',
      value: 'businessEntityTypeName',
    },
    {
      label: 'Email',
      value: 'businessEmail',
    },
    {
      label: 'Phone',
      value: 'primaryPhone',
      filter: value => value && maskPhone(value),
    },
    {
      label: 'Phone 2',
      value: 'otherPhone',
      filter: value => value && maskPhone(value),
    },
    {
      label: 'City/State',
      value: 'primaryCity',
      filter: (value, { primaryState }) => `${value} - ${primaryState}`,
    },
    {
      label: 'Address',
      value: 'primaryAddress',
    },
    {
      label: 'Address 2',
      value: 'primaryAddress2',
    },
    {
      label: 'Zip Code',
      value: 'primaryZipcode',
    },
    {
      label: 'Goods & Services',
      value: 'goodsAndServicesProvided',
      filter: value => value && stripHtml(value),
    },
    {
      label: 'Website',
      value: 'website',
      filter: value =>
        value && (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ),
    },
  ];

  const productsDataMap = [
    {
      label: 'Name',
      value: 'name',
      required: true,
    },
    {
      label: 'Batch #',
      value: 'batchNumber',
      required: true,
    },
    {
      label: 'Expiration Date',
      value: 'expirationDate',
      required: true,
      filter: value => value && dayjs(value).format(DATE_MONTH_DAY_YEAR),
    },
    {
      label: 'Files',
      value: 'coas',
      required: true,
      filter: value =>
        value.map((file, index) => (
          <span key={file.id}>
            {index > 0 && ', '}
            <a href={file.url} target="_blank" rel="noopener noreferrer">
              {getFileNameFromUrl(file.url)}
            </a>
          </span>
        )),
    },
  ];

  const documentsDataMap = [
    {
      label: 'Type',
      value: 'type',
      filter: id => id && findValue(documentTypes, { id }, 'name'),
    },
    {
      label: 'File',
      value: 'file',
      filter: value => (
        <a href={value} target="_blank" rel="noopener noreferrer">
          View <FontAwesomeIcon icon={['fal', 'external-link']} />
        </a>
      ),
    },
  ];

  const consentsDataMap = [
    {
      label: 'Type',
      value: 'type',
    },
    {
      label: 'Signed',
      value: 'isSigned',
      required: true,
      filter: value => (value ? 'Yes' : 'No'),
    },
  ];
  const loansDataMap = [
    {
      label: 'Id',
      value: 'id',
      required: true,
    },
    {
      label: 'Annual Sales $',
      value: 'loanAnnualSales',
      required: true,
    },
    {
      label: 'Buisness Net Income $',
      value: 'loanBuisnessNetIncome',
      required: true,
    },
    {
      label: 'Business Net Equity $',
      value: 'loanBusinessNetEquity',
      required: true,
    },
    {
      label: 'Number Of Employees',
      value: 'loanNumberOfEmployees',
      required: true,
    },
    {
      label: 'Automatic Deduction Account No.',
      value: 'loanAutomaticPaymentChecking',
      required: false,
    },
    {
      label: 'Type',
      value: 'loanType',
      required: true,
    },
    {
      label: 'Amount $',
      value: 'loanAmount',
      required: true,
    },
    {
      label: 'Repayment Period (months)',
      value: 'loanRepaymentPeriod',
      required: true,
    },
    {
      label: 'Use of Funds',
      value: 'loanUseOfFunds',
      required: true,
    },
    {
      label: 'Payment Amount',
      value: 'loanPaymentAmount',
      required: true,
    },
    {
      label: 'Loan Closed Date',
      value: 'loanClosedDate',
      required: true,
    },
  ];

  const obligationsDataMap = [
    {
      label: 'Id',
      value: 'id',
      required: true,
    },
    {
      label: 'Creditor',
      value: 'obligationCreditor',
      required: true,
    },
    {
      label: 'Date Opened',
      value: 'obligationDateOpened',
      filter: value => value && dayjs(value).format(DATE_MONTH_DAY_YEAR),
      required: true,
    },
    {
      label: 'Original Amount $',
      value: 'obligationOriginalAmount',
      required: true,
    },
    {
      label: 'Monthly Payment $',
      value: 'obligationMonthlyPayment',
      required: true,
    },
    {
      label: 'Secured By',
      value: 'obligationSecuredBy',
      required: true,
    },
    {
      label: 'Present Balance $',
      value: 'obligationPresentBalance',
      required: true,
    },
  ];

  const paymentDataMap = [
    {
      label: 'Bank Name',
      value: 'accountBankName',
    },
    {
      label: 'Bank Address',
      value: 'accountBankAddress',
    },
    {
      label: 'Account Number',
      value: 'accountAccountNumber',
    },
    {
      label: 'Routing Number',
      value: 'accountRoutingNumber',
    },
    {
      label: 'Name on Account',
      value: 'accountNameOnAccount',
    },

    {
      label: 'Bank Phone Number',
      value: 'accountBankPhoneNumber',
    },
    {
      label: 'Average Ticket',
      value: 'avgTicket',
    },
    {
      label: 'High Ticket',
      value: 'highTicket',
    },
    {
      label: 'Monthly Volume',
      value: 'monthlyVolume',
    },
  ];
  const fetchSubmissions = async (financialInstitutionId, accountId) => {
    const res = await getSubmissions(financialInstitutionId, accountId);

    if (res.ok) {
      setSubmissions(res.data);
    }

    return Promise.resolve(true);
  };

  useEffect(() => {
    async function load() {
      if (
        user.financialInstitutionId ||
        typeof account.financialInstitutions !== 'undefined'
      ) {
        await Promise.all([
          fetchAccount(user.accountId),

          fetchFormData(
            user.financialInstitutionId || account.financialInstitutions[0].id,
            user.accountId
          ),
          fetchSubmissions(
            user.financialInstitutionId || account.financialInstitutions[0].id,
            user.accountId
          ),
        ]);

        setLoading(false);
      }
    }

    setBreadcrumbs([{ label: 'Review and Submit', href: '/review' }]);

    load();
  }, []);

  const isConsentSigned = consentType => {
    const { consents } = account;
    return !!_.find(consents, { id: consentType.id });
  };

  useEffect(() => {
    let isError = false;

    if (account.isError()) {
      reduceServerErrors(account);
      isError = true;
    }

    if (formData.isError()) {
      reduceServerErrors(formData);
      isError = true;
    }

    async function getConsentTypes() {
      const consentTypes = await getConsentTypesForAccount(account, formData);
      setConsentTypes(
        consentTypes.map(ct => ({
          id: ct.id,
          type: ct.name,
          isSigned: isConsentSigned(ct) ? true : null,
        }))
      );
    }

    if (!isError) {
      getConsentTypes();
    }
  }, [account, formData]);

  useEffect(() => {
    setInitialValues({
      explanationBusiness: account.explanationBusiness,
      explanationLocations: account.explanationLocations,
      explanationPeople: account.explanationPeople,
      explanationVendors: account.explanationVendors,
      explanationProducts: account.explanationProducts,
      explanationDocuments: account.explanationDocuments,
      explanationConsents: account.explanationConsents,
      explanationLoans: account.explanationLoans,
      explanationObligations: account.explanationObligations,
    });
  }, [account]);

  const renderTab = (index, title, isComplete) => {
    let icon = 'check-circle';
    let color = 'gray';

    if (!loading) {
      icon = isComplete ? 'check-circle' : 'exclamation-circle';
    }

    if (!loading) {
      color = isComplete ? '#65dba6' : '#e75780';
    }

    return (
      <Tab isActive={index === activeTab}>
        <TabLink onClick={() => setActiveTab(index)}>
          <FontAwesomeIcon icon={['fas', icon]} size="lg" color={color} />
          &nbsp;&nbsp; {title}
        </TabLink>
      </Tab>
    );
  };

  const renderLastSubmission = () => {
    if (submissions.length === 0) {
      return null;
    }

    const lastSubmission = submissions[submissions.length - 1];
    return (
      <div className="last-submission">
        {`Last submission: ${lastSubmission.userFirstName} ${
          lastSubmission.userLastName
        } on ${dayjs(lastSubmission.date).format(DATE_DAY_SMONTH_YEAR)}`}
      </div>
    );
  };

  function tabContentWidth() {
    if (user.isBankAdmin) {
      const message = account[`explanation${activeTab}`];
      if (!message) {
        return 'full';
      }
    }
    return '1/2';
  }

  return (
    <>
      <Form
        onSubmit={user.isBankAdmin ? onUpdateAccountStatus : onSubmit}
        subscription={{
          submitting: true,
          hasValidationErrors: true,
        }}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, hasValidationErrors, form }) => {
          const submit = submitAndReset(handleSubmit, form);

          return (
            <form onSubmit={submit} noValidate className="review-and-submit">
              <InnerLoader visible={loading || submitting} />
              {renderLastSubmission()}
              <Card className="review-and-submit">
                <CardContent>
                  <header>
                    <legend className="title">Review &amp; Submit</legend>
                  </header>
                  <div className="description has-text-grey-medium-dark">
                    {user.isBankAdmin
                      ? 'The following information below has been provided by the merchant during the onboarding experience.'
                      : `We're still missing some information for your account.
                          Please review the following information and ensure it is
                          completed to the best of your ability before submitting to
                          underwriting team.`}
                  </div>
                  <UiSpacer />
                  <Tabs isToggle className="no-shadow no-margin">
                    <TabList>
                      {renderTab(
                        REVIEW_TAB_INDEXES.Business,
                        'Business',
                        isDataComplete(account, businessDataMap)
                      )}
                      {renderTab(
                        REVIEW_TAB_INDEXES.Locations,
                        'Locations',
                        isDataComplete(account.locations, locationsDataMap)
                      )}
                      {renderTab(
                        REVIEW_TAB_INDEXES.People,
                        'People',
                        isDataComplete(people, peopleDataMap)
                      )}
                      {renderTab(
                        REVIEW_TAB_INDEXES.Vendors,
                        'Vendors',
                        isDataComplete(account.vendors, vendorsDataMap)
                      )}
                      {renderTab(
                        REVIEW_TAB_INDEXES.Products,
                        'Products',
                        isDataComplete(account.products, productsDataMap)
                      )}
                      {hasConsents &&
                        renderTab(
                          REVIEW_TAB_INDEXES.Consents,
                          'Consents',
                          isDataComplete(consentTypes, consentsDataMap)
                        )}
                      {hasDocuments &&
                        renderTab(
                          REVIEW_TAB_INDEXES.Documents,
                          'Documents',
                          isDataComplete(account.documents, documentsDataMap)
                        )}
                      {hasPayment &&
                        renderTab(
                          REVIEW_TAB_INDEXES.Payment,
                          'Payment',
                          paymentDataMap
                        )}
                      {hasLoans &&
                        renderTab(
                          REVIEW_TAB_INDEXES.Loans,
                          'Loans',
                          isDataComplete(account.loans, loansDataMap)
                        )}
                      {hasObligations &&
                        renderTab(
                          REVIEW_TAB_INDEXES.Obligations,
                          'Obligations',
                          isDataComplete(
                            account.obligations,
                            obligationsDataMap
                          )
                        )}
                    </TabList>
                  </Tabs>
                </CardContent>
              </Card>
              <UiSpacer />
              {!loading && (
                <>
                  <Card className="review-and-submit">
                    <CardContent>
                      <Columns isMultiline>
                        <Column isSize={tabContentWidth()}>
                          {activeTab === REVIEW_TAB_INDEXES.Business && (
                            <DataIterator
                              title="Business"
                              dataMap={businessDataMap}
                              items={account}
                              itemKey="id"
                            />
                          )}
                          {activeTab === REVIEW_TAB_INDEXES.Locations &&
                            account.locations && (
                              <DataIterator
                                title="Locations"
                                subtitle="type"
                                dataMap={locationsDataMap}
                                columns={2}
                                items={account.locations}
                                itemKey="id"
                                viewLink={
                                  !user.isBankReadOnly
                                    ? '/locations/edit/'
                                    : null
                                }
                              />
                            )}
                          {activeTab === REVIEW_TAB_INDEXES.People &&
                            account.people && (
                              <DataIterator
                                title="People"
                                subtitle={item => (
                                  <>
                                    {_.get(item, 'fullName', '')}
                                    {item.isAdmin && (
                                      <span className="icon-tag">
                                        <FontAwesomeIcon
                                          icon={['fas', 'star']}
                                        />
                                      </span>
                                    )}
                                    {item.isSigner && (
                                      <span className="icon is-small has-text-grey icon-signer">
                                        <FontAwesomeIcon
                                          icon={['far', 'signature']}
                                        />
                                      </span>
                                    )}
                                  </>
                                )}
                                dataMap={peopleDataMap}
                                columns={2}
                                items={people}
                                itemKey="id"
                                viewLink={
                                  !user.isBankReadOnly ? '/people/edit/' : null
                                }
                              />
                            )}
                          {activeTab === REVIEW_TAB_INDEXES.Vendors &&
                            account.vendors && (
                              <DataIterator
                                title="Vendors"
                                subtitle="businessName"
                                dataMap={vendorsDataMap}
                                columns={2}
                                items={account.vendors}
                                itemKey="id"
                                viewLink="/vendors/edit/"
                              />
                            )}
                          {activeTab === REVIEW_TAB_INDEXES.Products &&
                            account.products && (
                              <DataIterator
                                title="Products"
                                subtitle="name"
                                dataMap={productsDataMap}
                                columns={2}
                                items={account.products}
                                itemKey="id"
                              />
                            )}
                          {hasDocuments &&
                            activeTab === REVIEW_TAB_INDEXES.Documents &&
                            account.documents && (
                              <DataIterator
                                title="Documents"
                                subtitle="title"
                                dataMap={documentsDataMap}
                                columns={2}
                                items={account.documents}
                                itemKey="id"
                              />
                            )}
                          {activeTab === REVIEW_TAB_INDEXES.Payment && (
                            <DataIterator
                              title="Payment"
                              dataMap={paymentDataMap}
                              items={account.payment}
                              itemKey="id"
                            />
                          )}
                          {hasConsents &&
                            activeTab === REVIEW_TAB_INDEXES.Consents &&
                            consentTypes && (
                              <DataIterator
                                title="Consents"
                                subtitle="type"
                                dataMap={consentsDataMap}
                                columns={2}
                                items={consentTypes}
                                itemKey="id"
                              />
                            )}
                          {activeTab === REVIEW_TAB_INDEXES.Loans &&
                            account.loans && (
                              <DataIterator
                                title="Loans"
                                subtitle={item => (
                                  <>
                                    {<span> ID: {_.get(item, 'id', '')}</span>}
                                    {
                                      <span>
                                        {' '}
                                        Loan Amount:{' '}
                                        {_.get(item, 'loanAmount', '')}
                                      </span>
                                    }
                                  </>
                                )}
                                dataMap={loansDataMap}
                                columns={2}
                                items={account.loans}
                                itemKey="id"
                                viewLink={
                                  !user.isBankReadOnly ? '/loans/edit/' : null
                                }
                              />
                            )}
                          {activeTab === REVIEW_TAB_INDEXES.Obligations &&
                            account.obligations && (
                              <DataIterator
                                title="Obligations"
                                subtitle={item => (
                                  <>
                                    {<span> ID: {_.get(item, 'id', '')}</span>}
                                    {
                                      <span>
                                        {' '}
                                        -{' '}
                                        {_.get(item, 'obligationCreditor', '')}
                                      </span>
                                    }
                                  </>
                                )}
                                dataMap={obligationsDataMap}
                                columns={2}
                                items={account.obligations}
                                itemKey="id"
                                viewLink={
                                  !user.isBankReadOnly
                                    ? '/obligations/edit/'
                                    : null
                                }
                              />
                            )}
                          <UiSpacer />
                        </Column>
                        {(user.isMerchantAdmin ||
                          account[`explanation${activeTab}`]) && (
                          <Column isSize="1/2">
                            <div className="subtitle">Explanation</div>
                            <div className="content">
                              <p className="description has-text-grey-medium-dark">
                                Please provide any additional notes for the
                                underwriter regarding this section. Include a
                                brief explanation why any information is missing
                                from this section.
                              </p>
                            </div>
                            {account[`explanation${activeTab}`] && (
                              <Notification
                                className="is-info content"
                                dangerouslySetInnerHTML={{
                                  __html: account[`explanation${activeTab}`],
                                }}
                              />
                            )}
                            {user.isMerchantAdmin && (
                              <>
                                <UiSpacer />
                                <Field
                                  key={activeTab}
                                  label=""
                                  component={UiQuillAdapter}
                                  name={`explanation${activeTab}`}
                                  id={`explanation${activeTab}`}
                                  style={{ minHeight: '300px' }}
                                />
                              </>
                            )}
                          </Column>
                        )}
                      </Columns>
                    </CardContent>
                  </Card>
                  <UiSpacer />
                  <UiSpacer />
                  {user.isMerchantAdmin ? (
                    <Columns isVCentered>
                      <Column>
                        <Field
                          component={UiToggleAdapter}
                          name="reviewed"
                          id="reviewed"
                          type="checkbox"
                          required
                          validate={
                            !user.isBankAdmin ? required('Disclaimer') : null
                          }
                          disabled={submitted}
                        >
                          Disclaimer
                          <br />
                          <div className="description">
                            You verify that all the required information has
                            been provided and acknowledge that everything is
                            true and accurate.
                            <br />
                            Failure to provide the required information may lead
                            to delays and potential cancellation of the
                            onboarding request.
                          </div>
                        </Field>
                      </Column>
                      <Column isSize="narrow">
                        <Columns isVCentered className="initials-fields">
                          <Column className="has-text-right">
                            <label
                              htmlFor="initials"
                              className="has-text-weight-semibold is-size-5 is-grey"
                            >
                              Initials
                            </label>
                          </Column>
                          <Column isSize="narrow">
                            <Field
                              component={UiInputAdapter}
                              name="initials"
                              id="initials"
                              required
                              validate={required('Initials')}
                              hideHelp
                              maxLength="2"
                              disabled={submitted}
                            />
                          </Column>
                          <Column isSize="narrow">
                            <Button
                              disabled={hasValidationErrors || submitted}
                              isColor="gradient-bright"
                              className="is-long"
                              type="submit"
                              isSize="medium"
                            >
                              Submit
                            </Button>
                          </Column>
                        </Columns>
                      </Column>
                    </Columns>
                  ) : (
                    <div className="buttons is-right bank-admin-tools">
                      {!user.isBankReadOnly && (
                        <>
                          <Button
                            isColor="danger"
                            type="button"
                            onClick={() =>
                              setUpdateAction(ACCOUNT_STATUS.SUBMITTED)
                            }
                            disabled={submitting}
                          >
                            <span className="icon">
                              <FontAwesomeIcon icon={['fal', 'times-circle']} />
                            </span>
                            <span>Deny</span>
                          </Button>
                          <Button
                            isColor="success"
                            type="button"
                            onClick={() =>
                              setUpdateAction(ACCOUNT_STATUS.ACTIVE)
                            }
                            disabled={submitting}
                          >
                            <span className="icon">
                              <FontAwesomeIcon icon={['fal', 'check-circle']} />
                            </span>
                            <span>Approve</span>
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
              {!user.isBankAdmin && (
                <SubmissionModal
                  title="Application Submitted"
                  content={
                    <>
                      <p>
                        Your application has been submitted to our underwriting
                        team. You will be notified via email once the
                        application is reviewed for next steps.
                      </p>
                      <p>
                        <b>Status:</b> Submitted
                      </p>
                    </>
                  }
                  onClickClose={() => setSubmissionModal(false)}
                  isActive={submissionModal}
                />
              )}
              {user.isBankAdmin && (
                <RespondModal
                  action={updateAction}
                  isActive={!!updateAction}
                  onClickClose={() => setUpdateAction('')}
                  hasValidationErrors={hasValidationErrors}
                />
              )}
            </form>
          );
        }}
      />
    </>
  );
}

ReviewAndSubmitListPage.propTypes = {
  user: propTypes.object.isRequired,
  account: propTypes.object.isRequired,
  formData: propTypes.object.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  fetchAccount: propTypes.func.isRequired,
  fetchFormData: propTypes.func.isRequired,
  signSubmission: propTypes.func.isRequired,
  updateAccountStatus: propTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    user: getUser(state),
    account: getAccount(state),
    formData: getFormData(state),
  };
}

const connectedReviewAndSubmitListPage = connect(mapStateToProps, {
  setBreadcrumbs,
  fetchAccount,
  fetchFormData,
  signSubmission,
  updateAccountStatus,
})(ReviewAndSubmitListPage);

export { connectedReviewAndSubmitListPage as ReviewAndSubmitListPage };
